/**
 * Ghost records (spec 6.6 / 4.3): a finished run's input log, serialized for
 * SaveStore. Determinism (Section 4.3) guarantees replaying the log
 * reproduces the identical run — desync-free by construction, enforced by
 * tests (ghost finishes at the identical tick).
 */
import { TUNING_VERSION } from "../config/tuning";
import type { RunRecord, InputLogEntry } from "./inputLog";

export interface GhostRecord {
  tuningVersion: number;
  levelId: string;
  /** Sim tick the recorded run started at (0 = full run). */
  startTick: number;
  /** End tick (finish or death) — playback hides the ghost after this. */
  endTick: number;
  timeMs?: number;
  entries: InputLogEntry[];
}

export function makeGhost(
  record: RunRecord,
  startTick: number,
  endTick: number,
  timeMs?: number,
): GhostRecord {
  return {
    tuningVersion: TUNING_VERSION,
    levelId: record.levelId,
    startTick,
    endTick,
    timeMs,
    entries: record.entries,
  };
}

export function serializeGhost(ghost: GhostRecord): string {
  return JSON.stringify(ghost);
}

/** Null on parse failure or tuning mismatch (old ghosts may not reproduce). */
export function deserializeGhost(raw: string): GhostRecord | null {
  try {
    const g = JSON.parse(raw) as GhostRecord;
    if (g.tuningVersion !== TUNING_VERSION) return null;
    if (!Array.isArray(g.entries)) return null;
    return g;
  } catch {
    return null;
  }
}
